import { useChannel } from '@ably-labs/react-hooks';
import { useQueryClient } from '@tanstack/react-query';
import { APP_CONSTANTS } from 'constants/app';
import { CHANNEL_MESSAGE_TYPES } from 'constants/common';
import useRefreshQueue from 'features/queue/newGrid/hooks/useRefreshQueue';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLoadedSrIdsWithValue } from 'remote-state/ticketServiceHooks';
import { QUERIES_KEYS } from '../../constant';
import { GRID_MODELS } from '../../features/queue/grid/constants';
import { getQueryKeyFromParams } from '../../features/queue/grid/helpers';
import { selectGridMode, selectSearchSrParams } from '../../features/queue/slice';
import {
  combineAndDecyrptUpdateState,
  createBulkItemsArray,
  createLockStatusData,
  isFiltersContainsUpdatedParams,
  processBulkItem,
  shouldUpdateSR,
  updateAssigneeField,
  updateCategoryField,
  updateDefaultField,
  updateServiceRequestCounts,
} from './utils/useQueueWebSocketDataSync.util';

const QUEUE_CHANNEL_NAME = localStorage.getItem(APP_CONSTANTS.ACCOUNT_ID_LOCAL_KEY);

// Hook implementation
export const useQueueWebSocketDataSync = () => {
  const refreshQueue = useRefreshQueue();
  const gridMode = useSelector(selectGridMode);
  const searchSrParams = useSelector(selectSearchSrParams);
  const queryKey = useMemo(() => getQueryKeyFromParams(searchSrParams), [searchSrParams]);

  const [queueUpdates, setQueueUpdates] = useState({
    existingSrs: {},
    updates: new Map([]),
    isEnabled: true,
  });

  const { data: loadedSrIdsWithKeys } = useLoadedSrIdsWithValue('insertTime');
  const queryClient = useQueryClient();
  const isZenModeOn = gridMode === GRID_MODELS.Zen;

  const invalidateQueryData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const updateColAccordingFieldType = useCallback(
    ({ field, newValues }) => {
      const fieldName = field.field;
      const updatedParams = newValues.params;

      // Block updates for fields that cannot be updated
      if (!updatedParams || !Object.keys(updatedParams).includes(fieldName)) return field;

      switch (fieldName) {
        case 'assignee':
          return updateAssigneeField(field, updatedParams);

        case 'primaryCategory':
        case 'secondaryCategory':
        case 'thirdLevelCategory':
          return updateCategoryField(field, newValues, fieldName, invalidateQueryData);

        default:
          return updateDefaultField(field, updatedParams, fieldName);
      }
    },
    [invalidateQueryData],
  );

  const findSrByIdAndUpdate = useCallback(
    ({ id, serviceRequests, newValues }) => {
      if (!Array.isArray(serviceRequests)) return serviceRequests;

      return serviceRequests.reduce((result, sr) => {
        const isUpdatedSr = shouldUpdateSR(sr, id);
        let updatedSR = sr;

        if (isUpdatedSr) {
          updatedSR = sr.map((field) => updateColAccordingFieldType({ field, newValues }));
        }

        return [...result, updatedSR];
      }, []);
    },
    [updateColAccordingFieldType],
  );

  const updateSRInCacheById = useCallback(
    ({ id, data }) => {
      queryClient.setQueryData(queryKey, (prevQueue) => {
        if (prevQueue?.serviceRequests && id && data) {
          const updatedServiceRequestsList = findSrByIdAndUpdate({
            id,
            serviceRequests: prevQueue?.serviceRequests,
            newValues: data,
          });
          return { ...prevQueue, serviceRequests: updatedServiceRequestsList };
        }
      });
    },
    [queryClient, queryKey, findSrByIdAndUpdate],
  );

  const unlockTicket = useCallback(
    ({ id }) => {
      queryClient.setQueryData([QUERIES_KEYS.TICKET_LOCK_STATUS, Number(id)], () => createLockStatusData(false));
    },
    [queryClient],
  );

  const lockTicket = useCallback(
    ({ data, id }) => {
      queryClient.setQueryData([QUERIES_KEYS.TICKET_LOCK_STATUS, Number(id)], () =>
        createLockStatusData(true, data?.userName),
      );
    },
    [queryClient],
  );

  const deleteTicket = useCallback(
    ({ id }) => {
      queryClient.setQueryData(queryKey, (prevQueue) => {
        let serviceRequests = prevQueue?.serviceRequests;

        if (Array.isArray(serviceRequests)) {
          const countsResult = updateServiceRequestCounts(prevQueue);

          serviceRequests = serviceRequests.filter((sr) => {
            const isFiltered = sr[0].value !== id;
            if (!isFiltered) {
              // Counts are already updated in countsResult
            }
            return isFiltered;
          });

          return { ...prevQueue, serviceRequests, ...countsResult };
        }

        return prevQueue;
      });
    },
    [queryClient, queryKey],
  );

  const updateCacheByType = useCallback(
    ({ id, name, data }) => {
      switch (name) {
        case CHANNEL_MESSAGE_TYPES.UPDATED:
          updateSRInCacheById({ id, data });
          break;

        case CHANNEL_MESSAGE_TYPES.LOCK_TICKET:
          lockTicket({ id, data });
          break;

        case CHANNEL_MESSAGE_TYPES.UNLOCK_TICKET:
          unlockTicket({ id });
          break;

        case CHANNEL_MESSAGE_TYPES.DELETE_TICKET:
          deleteTicket({ id });
          break;

        case CHANNEL_MESSAGE_TYPES.ADD_TICKET:
          invalidateQueryData();
          break;

        default:
          console.warn('Couldnt find appropiate action for given Websocket event', name, data);
          break;
      }
    },
    [lockTicket, unlockTicket, updateSRInCacheById, deleteTicket, invalidateQueryData],
  );

  // Utility function to handle queue updates state changes
  const handleQueueUpdatesChange = useCallback(
    (prev, data, name) => (prev?.isEnabled ? combineAndDecyrptUpdateState({ prev, data, name }) : prev),
    [],
  );

  // Utility function to handle SR update events
  const handleSRUpdateEvent = useCallback(
    (data, updateStateFunc) => {
      if (data.srParams) {
        const fieldUsage = isFiltersContainsUpdatedParams(data.srParams);

        if (fieldUsage.isInQueueColumsList) {
          if (fieldUsage.isUsedInFilters) {
            invalidateQueryData();
          } else {
            updateStateFunc();
          }
        }
        return true; // Event was handled
      }
      return false; // Event was not handled
    },
    [invalidateQueryData],
  );

  const [RealtimeChannel] = useChannel(QUEUE_CHANNEL_NAME, ({ data, name }) => {
    console.log('Websockets: useChannel emited', { data, name });

    const updateStateFunc = () => {
      if (setQueueUpdates) {
        setQueueUpdates((prev) => handleQueueUpdatesChange(prev, data, name));
      }
    };

    // Handle SR update events with special logic
    if (name === CHANNEL_MESSAGE_TYPES.UPDATED) {
      const wasHandled = handleSRUpdateEvent(data, updateStateFunc);
      if (wasHandled) return;
    }

    // Handle all other events
    updateStateFunc();
  });

  // Effect to sync loaded SR IDs
  useEffect(() => {
    setQueueUpdates((prev) => ({ ...prev, existingSrs: loadedSrIdsWithKeys }));
  }, [loadedSrIdsWithKeys]);

  // Effect to process queue updates
  useEffect(() => {
    let isNotUmounted = true;
    const updatesList = queueUpdates.updates;

    if (!isZenModeOn && updatesList.size !== 0) {
      const updateCache = async () => {
        const bulkItems = createBulkItemsArray(updatesList);
        const promises = bulkItems.map((item) =>
          processBulkItem(item, setQueueUpdates, updateCacheByType, isNotUmounted),
        );

        await Promise.all(promises).catch((e) =>
          console.error('Websockets: Error happened while applying events from websocket', e),
        );
      };

      updateCache();
      refreshQueue({ clearCache: false, showLoader: false });
    }

    return () => {
      isNotUmounted = false;
    };
  }, [isZenModeOn, queueUpdates, updateCacheByType, refreshQueue]);

  return { isConnectionFailed: RealtimeChannel.state === 'failed' };
};

export default useQueueWebSocketDataSync;
